import { useEffect, useState, useRef, useCallback } from 'react'
import s from './styles.module.scss'
import { head } from 'helpers'
import { Alert, Layouts, Loader, Input, TextArea, Form } from 'components'
import * as api from 'api'

export default function Main() {
  const [notificationType, setNotificationType] = useState('user') // 'user' or 'broadcast'
  const [userId, setUserId] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [redirect, setRedirect] = useState('')
  const [icon, setIcon] = useState('')
  const [image, setImage] = useState('')
  const [loading, setLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const processing = useRef(false)

  useEffect(() => {
    head({ title: 'Send Push Notification | BCN Admin' })
  }, [])

  const searchUsers = useCallback(
    async searchQuery => {
      if (processing.current || !searchQuery || searchQuery.length < 3) {
        setUsers([])
        return
      }

      processing.current = true
      setLoading(true)

      try {
        console.log('Searching users with query:', searchQuery) // Debug log
        
        // Build request object without undefined values
        const requestParams = {
          searchQuery: searchQuery,
          limit: 10,
          pageNo: 1
        }
        
        const response = await api.admin.user.fetchAll(requestParams)

        console.log('User search response:', response) // Debug log

        // Handle both success (200) and error (400) responses
        // Backend returns error when no users found, which is expected
        if (response) {
          if (response.code === 200 && response.payload) {
            const usersList = response.payload?.Users || response.payload?.users || []
            console.log('Found users:', usersList) // Debug log
            setUsers(usersList)
          } else if (response.code === 400 || response.code === 404) {
            // "Users not found" - this is expected when search doesn't match
            console.log('No users found for search:', searchQuery)
            setUsers([])
          } else {
            // Other error
            console.error('API error:', response)
            setUsers([])
            if (response.message && !response.message.toLowerCase().includes('not found')) {
              Alert.error(response.message || 'Failed to search users')
            }
          }
        } else {
          console.error('Empty response from API')
          setUsers([])
        }
      } catch (error) {
        console.error('Error searching users:', error)
        setUsers([])
        Alert.error('Failed to search users. Please check your connection and try again.')
      }

      setLoading(false)
      processing.current = false
    },
    []
  )

  useEffect(() => {
    if (userSearch) {
      const timeoutId = setTimeout(() => {
        searchUsers(userSearch)
      }, 500)

      return () => clearTimeout(timeoutId)
    } else {
      setUsers([])
    }
  }, [userSearch, searchUsers])

  const handleSendNotification = async () => {
    if (!title.trim()) {
      Alert.error('Please enter a notification title')
      return
    }

    if (!body.trim()) {
      Alert.error('Please enter a notification body')
      return
    }

    if (notificationType === 'user' && !userId) {
      Alert.error('Please select a user')
      return
    }

    setSending(true)

    try {
      let response

      if (notificationType === 'user') {
        response = await api.admin.pushNotification.send({
          userId,
          title: title.trim(),
          body: body.trim(),
          redirect: redirect.trim() || undefined,
          icon: icon.trim() || undefined,
          image: image.trim() || undefined
        })
      } else {
        response = await api.admin.pushNotification.broadcast({
          title: title.trim(),
          body: body.trim(),
          redirect: redirect.trim() || undefined,
          icon: icon.trim() || undefined,
          image: image.trim() || undefined
        })
      }

      if (response.code === 200) {
        Alert.success(response.message || 'Push notification sent successfully!')
        
        // Reset form
        setTitle('')
        setBody('')
        setRedirect('')
        setIcon('')
        setImage('')
        setSelectedUser(null)
        setUserId('')
        setUserSearch('')
      } else {
        Alert.error(response.message || 'Failed to send push notification')
      }
    } catch (error) {
      console.error('Error sending push notification:', error)
      Alert.error('Failed to send push notification. Please try again.')
    } finally {
      setSending(false)
    }
  }

  const handleUserSelect = user => {
    setSelectedUser(user)
    setUserId(user._id)
    setUserSearch(`${user.firstName} ${user.lastName} (${user.phoneNo})`)
    setUsers([])
  }

  return (
    <div className={s.main}>
      <Layouts.Classic title='Send Push Notification' />
      <div className={s.content}>
        <div className={s.formCard}>
          <Form>
            <div className={s.formSection}>
              <h3 className={s.sectionTitle}>Notification Type</h3>
              <div className={s.radioGroup}>
                <label className={s.radioLabel}>
                  <input
                    type='radio'
                    name='notificationType'
                    value='user'
                    checked={notificationType === 'user'}
                    onChange={e => {
                      setNotificationType(e.target.value)
                      setUserId('')
                      setSelectedUser(null)
                      setUserSearch('')
                    }}
                  />
                  <span>Send to Specific User</span>
                </label>
                <label className={s.radioLabel}>
                  <input
                    type='radio'
                    name='notificationType'
                    value='broadcast'
                    checked={notificationType === 'broadcast'}
                    onChange={e => setNotificationType(e.target.value)}
                  />
                  <span>Broadcast to All Users</span>
                </label>
              </div>
            </div>

            {notificationType === 'user' && (
              <div className={s.formSection}>
                <h3 className={s.sectionTitle}>Select User</h3>
                <div className={s.userSearch}>
                  <Input.Classic
                    type='text'
                    placeholder='Search by name, phone, or email...'
                    value={userSearch}
                    onChange={e => {
                      setUserSearch(e.target.value)
                      if (!e.target.value) {
                        setSelectedUser(null)
                        setUserId('')
                      }
                    }}
                    iconLeft='person'
                  />
                  {loading && (
                    <div className={s.loader}>
                      <Loader color='var(--c-primary)' colorText='var(--c-primary)' />
                    </div>
                  )}
                  {users.length > 0 && (
                    <div className={s.userDropdown}>
                      {users.map(user => (
                        <div
                          key={user._id}
                          className={s.userOption}
                          onClick={() => handleUserSelect(user)}
                        >
                          <div className={s.userInfo}>
                            <strong>
                              {user.firstName} {user.lastName}
                            </strong>
                            {user.phoneNo && <span>Phone: {user.phoneNo}</span>}
                            {user.email && <span>Email: {user.email}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {!loading && userSearch.length >= 3 && users.length === 0 && (
                    <div className={s.noUsersFound}>
                      No users found. Try a different search term.
                    </div>
                  )}
                  {selectedUser && (
                    <div className={s.selectedUser}>
                      <span className='material-icons-outlined'>check_circle</span>
                      <span>
                        Selected: {selectedUser.firstName} {selectedUser.lastName}
                        {selectedUser.phoneNo && ` (${selectedUser.phoneNo})`}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={s.formSection}>
              <h3 className={s.sectionTitle}>Notification Details</h3>
              <div className={s.formRow}>
                <Input.Classic
                  label='Title *'
                  type='text'
                  placeholder='Enter notification title'
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  iconLeft='title'
                />
              </div>
              <div className={s.formRow}>
                <TextArea.Classic
                  label='Body/Message *'
                  placeholder='Enter notification message...'
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={5}
                />
              </div>
            </div>

            <div className={s.formSection}>
              <h3 className={s.sectionTitle}>Optional Settings</h3>
              <div className={s.formRow}>
                <Input.Classic
                  label='Redirect URL'
                  type='text'
                  placeholder='e.g., /notifications, /businesses, etc.'
                  value={redirect}
                  onChange={e => setRedirect(e.target.value)}
                  iconLeft='link'
                />
              </div>
              <div className={s.formRow}>
                <Input.Classic
                  label='Icon URL'
                  type='text'
                  placeholder='e.g., /logo.png'
                  value={icon}
                  onChange={e => setIcon(e.target.value)}
                  iconLeft='image'
                />
              </div>
              <div className={s.formRow}>
                <Input.Classic
                  label='Image URL'
                  type='text'
                  placeholder='e.g., https://example.com/image.jpg'
                  value={image}
                  onChange={e => setImage(e.target.value)}
                  iconLeft='photo'
                />
              </div>
            </div>

            <div className={s.formActions}>
              {notificationType === 'user' && !userId && (
                <div className={s.warningText}>
                  Please select a user from the search results above
                </div>
              )}
              {!title.trim() && (
                <div className={s.warningText}>
                  Please enter a notification title
                </div>
              )}
              {!body.trim() && title.trim() && (
                <div className={s.warningText}>
                  Please enter a notification body
                </div>
              )}
              <button
                type='button'
                className={s.sendButton}
                onClick={handleSendNotification}
                disabled={sending || !title.trim() || !body.trim() || (notificationType === 'user' && !userId)}
                title={
                  sending
                    ? 'Sending...'
                    : !title.trim()
                    ? 'Please enter a title'
                    : !body.trim()
                    ? 'Please enter a body'
                    : notificationType === 'user' && !userId
                    ? 'Please select a user'
                    : 'Send notification'
                }
              >
                {sending ? (
                  <>
                    <Loader color='#fff' colorText='#fff' />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span className='material-icons-outlined'>send</span>
                    <span>
                      {notificationType === 'user' ? 'Send to User' : 'Broadcast to All'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  )
}

